import {
  API_PREFIX,
  HttpError,
  paginatedFromDto,
  type HttpClient,
} from "@nasam/kernel";
import type {
  DriverCreateInput,
  DriversRepository,
  DriverUpdateInput,
} from "../domain";
import {
  archivedDriverListResponseDtoSchema,
  archivedDriverMatchResponseDtoSchema,
  driverCreateDtoSchema,
  driverListResponseDtoSchema,
  driverMessageResponseDtoSchema,
  driverResponseDtoSchema,
  driversSummaryResponseDtoSchema,
  driverUpdateDtoSchema,
  type DriverCreateDto,
  type DriverUpdateDto,
} from "./driverDtos";
import {
  archivedDriverFromDto,
  archivedDriverMatchFromDto,
  driverCreateToDto,
  driverDetailsFromDto,
  driverFromDto,
  driverUpdateToDto,
} from "./driverMappers";

const DRIVERS_PATH = `${API_PREFIX}/drivers`;

// Only used once a file is being uploaded; without files a JSON body works
// too (see the POST /drivers swagger note).
function driverCreateMultipartBody(
  dto: DriverCreateDto,
  input: DriverCreateInput,
): FormData {
  const body = new FormData();
  for (const [key, value] of Object.entries(dto)) {
    if (value !== undefined) body.set(key, value);
  }
  if (input.profilePicture !== undefined) {
    body.set("profilePicture", input.profilePicture, input.profilePicture.name);
  }
  for (const document of input.documents ?? []) {
    body.append("documents", document, document.name);
  }
  return body;
}

// Same idea as on create. A cleared optional field (null in JSON) travels as
// an empty string, since form fields can't carry null.
function driverUpdateMultipartBody(
  dto: DriverUpdateDto,
  input: DriverUpdateInput,
): FormData {
  const body = new FormData();
  for (const [key, value] of Object.entries(dto)) {
    if (value !== undefined) body.set(key, value ?? "");
  }
  if (input.profilePicture !== undefined) {
    body.set("profilePicture", input.profilePicture, input.profilePicture.name);
  }
  for (const document of input.documents ?? []) {
    body.append("documents", document, document.name);
  }
  return body;
}

/**
 * Every `/drivers` endpoint the Driver Management screens use.
 */
export function createDriversHttpRepository(
  http: HttpClient,
): DriversRepository {
  return {
    // The query's names (search, status, from/toExpiryDate, sort, sortBy)
    // are the backend's own, so it goes out as-is.
    async list(query) {
      const response = await http.get(DRIVERS_PATH, { params: query });
      return paginatedFromDto(
        driverListResponseDtoSchema.parse(response),
        driverFromDto,
      );
    },

    // Driver-asset assignment doesn't exist yet, so `assigned` is 0 for now.
    async getStats() {
      const response = await http.get(`${DRIVERS_PATH}/summary`);
      const { data } = driversSummaryResponseDtoSchema.parse(response);
      return {
        total: data.totalDrivers,
        assigned: data.assignedDrivers,
        unassigned: data.unassignedDrivers,
        expiredLicenses: data.expiredLicenses,
      };
    },

    // The documents ride along, so there is no separate attachments call.
    async getById(id) {
      const response = await http.get(`${DRIVERS_PATH}/${id}`);
      return driverDetailsFromDto(driverResponseDtoSchema.parse(response).data);
    },

    async create(input) {
      const dto = driverCreateDtoSchema.parse(driverCreateToDto(input));
      const hasFiles =
        input.profilePicture !== undefined ||
        (input.documents ?? []).length > 0;
      try {
        const response = await http.post(
          DRIVERS_PATH,
          hasFiles ? driverCreateMultipartBody(dto, input) : dto,
        );
        const created = driverResponseDtoSchema.parse(response);
        return { status: "created", driver: driverFromDto(created.data) };
      } catch (error) {
        if (error instanceof HttpError && error.status === 409) {
          // ARCHIVED_DRIVER_FOUND: an archived driver holds the username or
          // email -- offer to restore it. Told apart by shape, like
          // departments.
          const archived = archivedDriverMatchResponseDtoSchema.safeParse(
            error.data,
          );
          if (archived.success) {
            return {
              status: "archived-match",
              match: archivedDriverMatchFromDto(archived.data.data),
              ...(archived.data.message === undefined
                ? {}
                : { message: archived.data.message }),
            };
          }
          // Otherwise a living driver holds a username, email, national ID,
          // RFID tag or license number; the localized message says which.
          return { status: "duplicate", message: error.message };
        }
        throw error;
      }
    },

    async update(id, input) {
      const dto = driverUpdateDtoSchema.parse(driverUpdateToDto(input));
      const hasFiles =
        input.profilePicture !== undefined ||
        (input.documents ?? []).length > 0;
      try {
        const response = await http.patch(
          `${DRIVERS_PATH}/${id}`,
          hasFiles ? driverUpdateMultipartBody(dto, input) : dto,
        );
        const updated = driverResponseDtoSchema.parse(response);
        return {
          status: "updated",
          driver: driverDetailsFromDto(updated.data),
        };
      } catch (error) {
        // A 409: the new RFID tag (or another identifier) is taken.
        if (error instanceof HttpError && error.status === 409) {
          return { status: "duplicate", message: error.message };
        }
        throw error;
      }
    },

    // The backend archives (soft delete); `restore` brings the driver back.
    // A 404 (gone, or another company's) surfaces as an HttpError with the
    // backend's localized message, which the global toast shows.
    async delete(id) {
      const response = await http.delete(`${DRIVERS_PATH}/${id}`);
      driverMessageResponseDtoSchema.parse(response);
    },

    async listArchived(query) {
      const response = await http.get(`${DRIVERS_PATH}/archived`, {
        params: query,
      });
      return paginatedFromDto(
        archivedDriverListResponseDtoSchema.parse(response),
        archivedDriverFromDto,
      );
    },

    // Refused with 409 (an identifier was taken meanwhile), 404 (the branch
    // was archived) or 400 (the branch was deactivated); the HttpError keeps
    // the backend's localized reason.
    async restore(id) {
      const response = await http.patch(`${DRIVERS_PATH}/${id}/restore`);
      return driverDetailsFromDto(driverResponseDtoSchema.parse(response).data);
    },

    // Only for a driver still pending activation. Refused with 409 (already
    // activated or deactivated), 429 (throttled per mailbox) or 503 (the mail
    // could not be sent; the previous link keeps working).
    async resendActivation(id) {
      const response = await http.post(
        `${DRIVERS_PATH}/${id}/resend-activation-link`,
      );
      driverMessageResponseDtoSchema.parse(response);
    },
  };
}
